// Wraps the generic DataTable with column definitions specific to the bot's
// `actions` audit rows. Mirrors the typography + status badges used in
// the reference NavHistoryTable / TransactionLedger.
import type { ActionRow } from '../lib/api';
import { DataTable, type Column } from './ui/data-table';
import { StatBadge } from './ui/stat-badge';
import { cn, formatCurrency, formatDateTime } from '../lib/utils';
import { ExternalLink } from 'lucide-react';

/**
 * Extract all tx hashes from an action's structured fields.
 *
 * Different action kinds store hashes differently:
 *   - simple ops (hedge, harvest): single `txHash` on the row
 *   - multi-step ops (redeploy, manual mint): array under
 *     `payload.txs = [{name, hash}, ...]`
 *   - attribution may have specific named hashes (closeTx, mintTx)
 *
 * Returns hashes in execution order so the first one is typically the
 * most useful starting point for Basescan diagnostics.
 */
function extractTxHashes(r: ActionRow): { name: string; hash: string }[] {
  const out: { name: string; hash: string }[] = [];
  // Multi-tx action payload (redeploy, manual mint, etc.)
  const payloadTxs = r.payload?.txs as
    | Array<{ name?: string; hash?: string }>
    | undefined;
  if (Array.isArray(payloadTxs)) {
    for (const t of payloadTxs) {
      if (t.hash && /^0x[a-fA-F0-9]{64}$/.test(t.hash)) {
        out.push({ name: t.name ?? 'tx', hash: t.hash });
      }
    }
  }
  // Single tx on the action row.
  if (r.txHash && /^0x[a-fA-F0-9]{64}$/.test(r.txHash)) {
    if (!out.some((t) => t.hash === r.txHash)) {
      out.push({ name: 'tx', hash: r.txHash });
    }
  }
  return out;
}

/**
 * Tx column cell — shows a clickable link to Basescan for each hash.
 * Multi-tx actions get the first hash as the "primary" plus a "+N more"
 * affordance that expands to show all of them.
 */
function txCell(r: ActionRow): JSX.Element {
  const txs = extractTxHashes(r);
  if (txs.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  if (txs.length === 1) {
    const t = txs[0]!;
    return (
      <a
        href={`https://basescan.org/tx/${t.hash}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs inline-flex items-center gap-1 text-primary hover:underline font-mono"
        title={`${t.name}: ${t.hash}`}
      >
        {t.hash.slice(0, 6)}…{t.hash.slice(-4)}
        <ExternalLink className="h-3 w-3" />
      </a>
    );
  }
  // Multi-tx: show primary + tooltip with all
  const primary = txs[0]!;
  const allList = txs.map((t) => `${t.name}: ${t.hash}`).join('\n');
  return (
    <span
      className="inline-flex items-center gap-2 text-xs"
      title={allList}
    >
      <a
        href={`https://basescan.org/tx/${primary.hash}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline font-mono inline-flex items-center gap-1"
      >
        {primary.hash.slice(0, 6)}…{primary.hash.slice(-4)}
        <ExternalLink className="h-3 w-3" />
      </a>
      <span className="text-muted-foreground">+{txs.length - 1} more</span>
    </span>
  );
}

const KIND_LABEL: Record<string, string> = {
  hedge_order: 'manual hedge',
  auto_hedge: 'auto hedge',
  auto_redeploy: 'auto redeploy',
  auto_harvest: 'auto harvest',
  funding_accrual: 'funding',
};

/**
 * Build the per-action attribution text from the structured JSON.
 * Each kind has a different breakdown shape (see Phase 4 schema notes).
 */
function attributionText(r: ActionRow): string {
  const a = r.attribution;
  if (!a) {
    // Fall back to payload-based summaries for pre-Phase-4 rows.
    if (r.error) return r.error.split('\n')[0] ?? r.error;
    if (r.payload?.newNftId) return `→ NFT #${r.payload.newNftId as string}`;
    if (r.payload?.usdcReceived)
      return `+$${(Number(r.payload.usdcReceived as string) / 1e6).toFixed(2)} USDC`;
    return '';
  }

  if (r.kind === 'auto_harvest') {
    const aero = (a.aeroClaimedTokens as number | undefined) ?? 0;
    const usdcIn = (a.aeroUsdcIn as number | undefined) ?? 0;
    const gas = (a.gasUsd as number | undefined) ?? 0;
    return `${aero.toFixed(3)} AERO → $${usdcIn.toFixed(2)} − $${gas.toFixed(3)} gas`;
  }
  if (r.kind === 'auto_redeploy') {
    const oldId = a.oldNftId as string | undefined;
    const newId = a.newNftId as string | undefined;
    const gas = (a.gasUsd as number | undefined) ?? 0;
    const aero = (a.claimedAeroTokens as number | undefined) ?? 0;
    const aeroNote = aero > 0 ? `, claimed ${aero.toFixed(3)} AERO` : '';
    return `#${oldId} → #${newId}${aeroNote}, gas $${gas.toFixed(3)}`;
  }
  if (r.kind === 'auto_hedge') {
    const side = a.side as string | undefined;
    const fillSize = (a.fillSize as number | undefined) ?? 0;
    const px = (a.avgPrice as number | undefined) ?? 0;
    const fee = (a.feeUsdEst as number | undefined) ?? 0;
    // coin recorded since the multi-coin era; old rows are all ETH.
    const coin = (a.coin as string | undefined) ?? 'ETH';
    return `${side} ${fillSize.toFixed(6)} ${coin} @ $${px.toFixed(2)} (fee ~$${fee.toFixed(3)})`;
  }
  if (r.kind === 'funding_accrual') {
    const amt = (a.amountUsd as number | undefined) ?? 0;
    const rate = (a.rateBps8h as number | undefined) ?? 0;
    const sz = (a.positionSize as number | undefined) ?? 0;
    const coin = (a.coin as string | undefined) ?? '';
    const sign = amt >= 0 ? '+' : '−';
    return `${sign}$${Math.abs(amt).toFixed(4)} on ${sz.toFixed(4)} ${coin} (rate ${rate.toFixed(3)} bps/8h)`;
  }
  return '';
}

/**
 * Δ NAV cell. Surfaces the action's economic impact on strategy NAV.
 *
 * For LIVE on-chain actions (hedge/harvest/redeploy), the action happens
 * between two observation snapshots, so `nav_after − nav_before` captures
 * the impact + small market noise within the tick window. We show that.
 *
 * For BACKFILLED historical events (funding accruals — the funding tracker
 * ingests events from hours ago), the snapshot-diff would just be market
 * noise between when we INGESTED the event and the next observation —
 * misleading. Instead we show `attribution.amountUsd` for those rows,
 * which is the realized P&L of the event itself.
 */
function navDeltaCell(r: ActionRow) {
  // Backfilled historical events: prefer the attribution-reported impact.
  if (r.kind === 'funding_accrual') {
    const amt = r.attribution?.amountUsd as number | undefined;
    if (amt === undefined) {
      return <span className="text-xs text-muted-foreground">—</span>;
    }
    const color =
      amt > 0
        ? 'text-positive'
        : amt < 0
        ? 'text-negative'
        : 'text-muted-foreground';
    const sign = amt > 0 ? '+' : '';
    return (
      <span
        className={cn('text-xs tabular-nums font-medium', color)}
        title="Realized funding impact (from HL settlement)"
      >
        {sign}
        {formatCurrency(amt)}
      </span>
    );
  }

  // Live actions: snapshot delta.
  if (r.navBeforeUsd == null || r.navAfterUsd == null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const delta = r.navAfterUsd - r.navBeforeUsd;
  const color =
    delta > 0
      ? 'text-positive'
      : delta < 0
      ? 'text-negative'
      : 'text-muted-foreground';
  const sign = delta > 0 ? '+' : '';
  return (
    <span
      className={cn('text-xs tabular-nums font-medium', color)}
      title={`before $${r.navBeforeUsd.toFixed(2)} → after $${r.navAfterUsd.toFixed(2)}`}
    >
      {sign}
      {formatCurrency(delta)}
    </span>
  );
}

function statusBadge(status: string) {
  if (status === 'confirmed') return <StatBadge label="Confirmed" variant="positive" dot />;
  if (status === 'failed') return <StatBadge label="Failed" variant="negative" dot />;
  if (status === 'pending') return <StatBadge label="Pending" variant="pending" dot />;
  return <StatBadge label={status} variant="neutral" dot />;
}

const columns: Column<ActionRow>[] = [
  {
    key: 'ts',
    header: 'Time',
    cell: (r) => (
      <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(r.ts)}</span>
    ),
  },
  {
    key: 'kind',
    header: 'Kind',
    cell: (r) => <span className="text-foreground">{KIND_LABEL[r.kind] ?? r.kind}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    cell: (r) => statusBadge(r.status),
  },
  {
    key: 'navDelta',
    header: 'Δ NAV',
    cell: navDeltaCell,
  },
  {
    key: 'detail',
    header: 'Detail',
    cell: (r) => {
      const detail = attributionText(r);
      return (
        <span
          className={cn(
            'text-xs max-w-md truncate inline-block',
            r.error ? 'text-negative' : 'text-muted-foreground',
          )}
          title={detail}
        >
          {detail}
        </span>
      );
    },
  },
  {
    key: 'tx',
    header: 'Tx',
    cell: txCell,
  },
];

interface Props {
  rows: ActionRow[];
  pageSize?: number;
  emptyMessage?: string;
}

export function ActionsTable({ rows, pageSize, emptyMessage }: Props) {
  return (
    <DataTable
      columns={columns}
      data={rows}
      keyExtractor={(r) => String(r.id)}
      pageSize={pageSize}
      emptyMessage={emptyMessage}
    />
  );
}
